/**
 * 작성자 : 홍선영
 * 날짜 : 2023.07.05
 * 경로 : 출역 관리 - 개인별 월 출역현황
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { PulseLoader } from 'react-spinners';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import { IoChevronUpSharp } from 'react-icons/io5';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { IModal, ITabObject } from 'customTypes';
import { pageInfoState, themeState, userState } from '../../atoms';
import { MONTH_LIST, YEAR_LIST } from '../../_constants';
import { dark, light } from '../../assets/styles/theme';
import { SearchOptions } from '../../assets/styles/SearchOptions';
import { FloatingButtonToTop } from '../../assets/styles/FloatingButtonToTop';
import { ContentsContainerRoot } from '../../assets/styles/ContentsContainerRoot';
import { LoadingModalBackground, ModalBackground } from '../../assets/styles/Modal';
import { BtnGhost, BtnLightViolet } from '../../components/Button';
import Tab from '../../components/Tab';
import Portal from '../../components/Portal';
import Toggle from '../../components/Toggle';
import WorkerListModal from '../../components/Modal/WorkerListModal';
import SearchSelectBoxSm from '../../components/SearchSelectBoxSm';
import AttendDailyPersonalListModal from '../../components/Modal/AttendDailyPersonalListModal';
import { useSetAuth } from '../../utils/useSetAuth';
import { trimObject } from '../../utils/trimObject';
import { useSetSjcd } from '../../utils/useSetSjcd';
import { todayYYYYMMDD } from '../../utils/formatDate';
import { scrollToNodeTop } from '../../utils/scrollToNodeTop';
import { useDetectScrolledToBottom } from '../../utils/useDetectScrolledToBottom';
import useSetJobtype from '../../utils/useSetJobtype';
import useOnKeydownF9 from '../../utils/useOnKeydownF9';
import { logPost } from '../../services/log';
import i18n from '../../translation/i18n';
import { apiGet } from '../../services/_common';
import SelectBox from '../../components/SelectBox';
import ShortcutButton from '../../components/button/ShortcutButton';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { showExcelButton } from '../../utils/deviceAuth';

const Root = styled(ContentsContainerRoot)`
  display: flex;
  flex-direction: column;
  .btn {
    button {
      height: 2.5rem;
      padding: 0 0.75rem;
      font-size: 0.875rem;
    }
  }
  > div:nth-child(2) {
    overflow: auto;
  }
  .searchData {
    .mobile-hidden {
      @media screen and (max-width: 768px) {
        display: none;
      }
    }
  }
  .calendarMonthTitle {
    text-align: center;
    margin-top: 1rem;
    color: var(--text-primary);
    gap: 0.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;

    > div {
      width: 3rem;
      height: 3rem;
      border-radius: 5px;
      :hover {
        color: var(--primary);
        cursor: pointer;
      }
    }

    .month-title {
      min-width: 15rem;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 1.5rem;
      padding: 0 0.5rem;
      width: fit-content;
      cursor: pointer;
    }
  }

  .calendar {
    display: grid;
    overflow: hidden;
    margin: 0.5rem;
    /* padding: 0.5rem; */
    border: 1px solid var(--outline);
    border-radius: 10px;
    overflow: auto;
    grid-template-columns: repeat(7, 1fr);
    gap: 0;

    @media screen and (max-width: 768px) {
      padding: 0.25rem;
    }

    .days {
      min-width: 0;
      text-align: center;
      padding: 0.5rem;
      font-weight: 700;
      border-left: 1px solid var(--outline);
      /* outline: 0.0625rem solid var(--outline); */
      background-color: var(--background);
      font-size: 0.875rem;

      @media screen and (max-width: 768px) {
        padding: 0.25rem;
        font-size: 0.75rem;
      }
    }

    .emptyCells {
      /* border: 0.0625rem solid var(--outline); */
      border-left: 1px solid var(--outline);
      border-top: 1px solid var(--outline);
    }

    .date {
      height: 12rem;
      font-weight: 600;
      /* outline: 0.0625rem solid var(--outline); */
      /* border: 0.0625rem solid var(--outline); */
      border-left: 1px solid var(--outline);
      border-top: 1px solid var(--outline);
      padding: 1rem;
      cursor: pointer;
      display: flex;
      flex-direction: column;

      &:nth-child(1n) {
      }

      @media screen and (max-width: 768px) {
        height: 8rem;
        padding: 0.5rem;
      }

      img {
        align-items: center;
        display: flex;
        justify-content: center;
        margin: 0 auto;
        height: calc(100% - 2rem);
        object-fit: contain;

        @media screen and (max-width: 768px) {
          height: calc(100% - 1.5rem);
        }
      }

      > div {
        height: 100%;
        > div:first-child {
          height: calc(100% - 0.6rem);
        }
        > div:nth-child(1) > div:nth-child(2) {
          color: var(--text-secondary);
        }
        > div:nth-child(2) {
          color: var(--text-tertiary);
          font-size: 0.875rem;

          @media screen and (max-width: 768px) {
            font-size: 0.75rem;
          }
        }
      }
    }
  }
`;

const MonthPickerPopupStyle = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-53%);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  background: var(--background);
  padding: 1rem;
  z-index: 1000;

  .year-month-selector {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .year-selector {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;

      select {
        padding: 0.25rem;
        border-radius: 4px;
        border: 1px solid var(--tonal);
        outline: none;

        &:focus {
          border-color: var(--primary);
        }
      }
    }

    .month-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 0.5rem;

      button {
        color: var(--text-tertiary);
        padding: 0.5rem;
        border: 1px solid var(--tonal);
        border-radius: 4px;
        background-color: transparent;
        cursor: pointer;

        &:hover {
          border: 1px solid var(--filled-blue-light);
        }

        &.selected {
          background-color: var(--tonal-blue);
        }
      }
    }
  }
`;

interface ISearchOption {
  wName: string;
  wCd: string;
}

const Sattend6 = () => {
  dayjs.locale(i18n.language === 'en-US' ? 'en' : 'ko');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const showExcelBtn = showExcelButton(auth.excelAuth);
  const { isF9Pressed, setIsF9Pressed } = useOnKeydownF9(); // f9키 프레스 훅
  const { siteJoinInfoList } = useSetSjcd();
  const size = useOutletContext<any>();
  const tabInfo = useRecoilValue(pageInfoState);
  const [currentTabMrCd, setCurrentTabMrCd] = useState(tabInfo.defaultMrCd);
  const toDay = todayYYYYMMDD();
  const userInfo = useRecoilValue(userState);
  const { userMenuList } = userInfo;
  const wNameRef = useRef<HTMLInputElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const [jikJongList, setJikJongList] = useState<any[]>([]);
  const [searchOption, setSearchOption] = useState<ISearchOption>({ wName: '', wCd: '' });
  const [sjCd, setSjCd] = useState({ type: 'sjCd', sjCd: '', cdName: '' });
  const [aYear, setAYear] = useState({ type: 'aYear', aYear: toDay.substring(0, 4), cdName: toDay.substring(0, 4) });
  const [month, setMonth] = useState({ type: 'month', month: toDay.substring(4, 6), cdName: toDay.substring(4, 6) });
  const [wPrejobtype, setWPrejobtype] = useState({ type: 'wPrejobtype', wPrejobtype: '', cdName: '' });
  const [wJobtype, setWJobtype] = useState({ type: 'wJobtype', wJobtype: '', cdName: '' });
  const { prejobtypeList, jobtypeList } = useSetJobtype(wPrejobtype.wPrejobtype);
  const [openModal, setOpenModal] = useState<IModal>({ status: false, type: '', code: { wCd: '' }, isOverlappingModal: false });
  const [selectedUser, setSelectedUser] = useState<any | null>(null); // 근로자 검색팝업에서 선택한 유저
  const [searchCheck, setSearchCheck] = useState<boolean>(false); // 근로자 검색절차 완료 여부
  const [photoViewYn, setPhotoViewYn] = useState(true);
  const { isBottom } = useDetectScrolledToBottom(tableRef);
  const [newTabList, setNewTabList] = useState<ITabObject[]>();
  const theme = useRecoilValue(themeState);
  const [loading, setLoading] = useState(false);
  const todayjs = dayjs();
  const datePickerRef = useRef<HTMLDivElement>(null);

  const searchMonthAttendAPI = async (wCd: string, y: string, m: string) => {
    setCalendarMonth(m);
    setSelectedYear({ type: 'aYear', aYear: y, cdName: y });
    // setFirstDayOfMonth(dayjs(`${y}-${m}-01`).day());
    // const totalDays = dayjs(`${y}-${m}-01`).day() + dateArray.length;
    // setRemainingCells(7 - (totalDays % 7) === 7 ? 0 : totalDays % 7);
    // setRemainingCells(totalDays < 35 ? 35 - totalDays : 42 - totalDays);

    if (wCd) {
      setLoading(true);

      try {
        const obj = { wCd, aMonth: `${y}${m}` };
        const req = { ...obj, hCd: userInfo.hCd, sCd: userInfo.sCd };
        const res = await apiGet({ path: '/attend/monthDetail', req });

        const { data, statusCode } = res.data;
        if (statusCode === 200) {
          setLoading(false);

          const getDateArray2 = () => {
            let startDay = dayjs(`${y}-${m}-01`);
            const endDay = startDay.endOf('month');
            const dateArray: any = [];

            while (startDay.isBefore(endDay)) {
              dateArray.push({ date: startDay.format('DD'), dayOfWeek: startDay.day(), aImg: '', aTimeMin: '', aTimeMax: '' });
              startDay = startDay.add(1, 'day');
            }
            return dateArray;
          };

          const newFirstDayOfMonth = dayjs(`${y}-${m}-01`).day();
          const newDateArray = getDateArray2();

          if (data.attendList.length > 0) {
            const updatedDates = newDateArray.map((dateObj: any) => {
              const attendItem = data.attendList.find((attend: any) => attend.aDate.substring(6, 8) === dateObj.date);
              if (attendItem) {
                return {
                  ...dateObj,
                  aTimeMin: attendItem.aTimeMin,
                  aTimeMax: attendItem.aTimeMax,
                  aImg: attendItem.aImg,
                };
              }
              return dateObj;
            });
            const newTotalDays = newFirstDayOfMonth + updatedDates.length;
            setDateArray(updatedDates);
            setFirstDayOfMonth(newFirstDayOfMonth);
            setTotalDays(newTotalDays);
            setRemainingCells(7 - (newTotalDays % 7) === 7 ? 0 : 7 - (newTotalDays % 7));
          } else {
            toast.info(t('출역데이터가 없습니다.'));
            const newTotalDays = newFirstDayOfMonth + newDateArray.length;
            setDateArray(newDateArray);
            setFirstDayOfMonth(newFirstDayOfMonth);
            setTotalDays(newTotalDays);
            setRemainingCells(7 - (newTotalDays % 7) === 7 ? 0 : 7 - (newTotalDays % 7));
          }
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error(error);
        throw new Error('error');
      }
    } else toast.warn(t('근로자를 선택하세요'));
  };

  const handleMonthChange = async (type: 'prev' | 'next') => {
    if (!selectedUser) return toast.warn(t('근로자를 선택하세요'));

    let currentYear = parseInt(aYear.aYear, 10);
    let currentMonth = parseInt(month.month, 10);

    if (type === 'prev') {
      currentMonth -= 1;
      if (currentMonth < 1) {
        currentMonth = 12;
        currentYear -= 1;
      }
    } else if (type === 'next') {
      currentMonth += 1;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear += 1;
      }
    }

    const newYear = currentYear.toString();
    const newMonth = currentMonth.toString().padStart(2, '0');

    setMonth({ type: 'month', month: newMonth, cdName: newMonth });
    setAYear({ type: 'aYear', aYear: newYear, cdName: newYear });

    return searchMonthAttendAPI(selectedUser.wCd, newYear, newMonth);
  };

  useEffect(() => {
    const arr: any[] = [];
    userMenuList.map((v: any) => {
      const result = tabInfo?.tabList?.find((el: ITabObject) => el.mtCd === v.mtCd && el.mdCd === v.mdCd && el.mrCd === v.mrCd);
      if (result) {
        arr.push(result);
        setNewTabList(arr);
      }
    });
  }, [tabInfo.tabList]);

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const getDateArray = useCallback(() => {
    let startDay = dayjs(`${aYear.aYear}-${month.month}-01`);
    const endDay = startDay.endOf('month');
    const dateArray: any = [];

    while (startDay.isBefore(endDay)) {
      // dateArray.push(startDay.format('DD ddd'));
      dateArray.push({ date: startDay.format('DD'), dayOfWeek: startDay.day(), aImg: '', aTimeMin: '', aTimeMax: '' });
      startDay = startDay.add(1, 'day');
    }
    return dateArray;
  }, [aYear.aYear, month.month]);

  const [dateArray, setDateArray] = useState(getDateArray);
  const [firstDayOfMonth, setFirstDayOfMonth] = useState(dayjs(`${aYear.aYear}-${month.month}-01`).day());
  const [totalDays, setTotalDays] = useState(firstDayOfMonth + dateArray.length);
  const [remainingCells, setRemainingCells] = useState(7 - (totalDays % 7) === 7 ? 0 : 7 - (totalDays % 7));
  // const [remainingCells, setRemainingCells] = useState(totalDays < 35 ? 35 - totalDays : 42 - totalDays);
  const [calendarMonth, setCalendarMonth] = useState(toDay.substring(4, 6));
  const [monthTitle, setMonthTitle] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState({ type: 'aYear', aYear: aYear.aYear, cdName: aYear.cdName });
  const [initiate, setinitiate] = useState('');
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  useEffect(() => {
    // setDateArray(getDateArray);
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '개인별 월 출역현황',
      action: '조회',
      etc: ``,
    });
  }, []);

  useEffect(() => {
    const result = dayjs(`${aYear.aYear}-${month.month}-01`);
    setMonthTitle(result.format(i18n.language === 'en-US' ? `YYYYMMMM` : `YYYY년 MM월`));
  }, [calendarMonth, aYear.aYear, i18n.language]);

  useEffect(() => {
    if (isF9Pressed) {
      // 근로자검색 모달에서 F9를 눌렀을 때 월출역보고서의 F9가 트리거되지 않도록 처리함
      if (!openModal.status) onClickSearch();
      setIsF9Pressed(false);
    }
  }, [isF9Pressed]);

  /*
  useEffect(() => {
    setTotalDays(firstDayOfMonth + dateArray.length);
  }, [firstDayOfMonth]);

  useEffect(() => {
    setRemainingCells(totalDays < 35 ? 35 - totalDays : 42 - totalDays);
  }, [totalDays]);
  */

  useEffect(() => {
    setWJobtype({ type: 'wJobtype', wJobtype: '', cdName: '' });
    if (wPrejobtype.wPrejobtype === '') setJikJongList([]);
  }, [wPrejobtype.wPrejobtype]);

  useEffect(() => {
    setJikJongList(jobtypeList);
  }, [jobtypeList]);

  useEffect(() => {
    // 근로자 검색 팝업에서 선택버튼까지 클릭했을 때 setState
    if (selectedUser && (selectedUser.sjCd !== '' || selectedUser.wName !== '')) {
      if (!searchCheck) {
        getMonthlyAttendListAPI(selectedUser.wCd);
      }
      // setFirstDayOfMonth(dayjs(`${aYear.aYear}-${month.month}-01`).day());
      setCalendarMonth(month.month);
      setSearchOption((prev: ISearchOption) => ({ ...prev, wName: selectedUser.wName, wCd: selectedUser.wCd }));
      setSearchCheck(true);
    }
  }, [selectedUser]);

  useEffect(() => {
    // 근로자 검색을 마친 상태에서 협력업체나 근로자명을 변경했을 때 검색여부 false로 변경
    if (selectedUser) {
      if (selectedUser.wName !== searchOption.wName || selectedUser.sjCd !== sjCd.sjCd || selectedUser.wCd !== searchOption.wCd) {
        setSearchCheck(false);
      }
    }
  }, [searchOption.wName, sjCd.sjCd]);

  // 월출역현황 조회
  const getMonthlyAttendListAPI = async (wCdParam: string) => {
    const obj = { wCd: wCdParam, aMonth: `${aYear.aYear}${month.month}` };
    const req = { ...obj, hCd: userInfo.hCd, sCd: userInfo.sCd };
    const res = await apiGet({ path: '/attend/monthDetail', req });

    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      setLoading(false);
      const newFirstDayOfMonth = dayjs(`${aYear.aYear}-${month.month}-01`).day();
      const newDateArray = getDateArray();

      if (data.attendList.length > 0) {
        const updatedDates = newDateArray.map((dateObj: any) => {
          const attendItem = data.attendList.find((attend: any) => attend.aDate.substring(6, 8) === dateObj.date);
          if (attendItem) {
            return {
              ...dateObj,
              aTimeMin: attendItem.aTimeMin,
              aTimeMax: attendItem.aTimeMax,
              aImg: attendItem.aImg,
            };
          }
          return dateObj;
        });
        const newTotalDays = newFirstDayOfMonth + updatedDates.length;
        setDateArray(updatedDates);
        setFirstDayOfMonth(newFirstDayOfMonth);
        setTotalDays(newTotalDays);
        setRemainingCells(7 - (newTotalDays % 7) === 7 ? 0 : 7 - (newTotalDays % 7));
      } else {
        toast.info(t('출역데이터가 없습니다.'));
        const newTotalDays = newFirstDayOfMonth + newDateArray.length;
        setDateArray(newDateArray);
        setFirstDayOfMonth(newFirstDayOfMonth);
        setTotalDays(newTotalDays);
        setRemainingCells(7 - (newTotalDays % 7) === 7 ? 0 : 7 - (newTotalDays % 7));
      }
    } else {
      setLoading(false);
      // toast.error(t(ERROR));
    }
  };

  const getWorkerListAPI = async (wCdParam: string) => {
    const newObj = { sjCd: sjCd.sjCd, wName: searchOption.wName, wPrejobtype: wPrejobtype.wPrejobtype, wJobtype: wJobtype.wJobtype };
    const trimData = trimObject(newObj);
    const req = { ...trimData, hCd: userInfo.hCd, sCd: userInfo.sCd };
    const res = await apiGet({ path: '/worker', req });
    const { statusCode, data } = res.data;
    if (statusCode === 200) {
      setFirstDayOfMonth(dayjs(`${aYear.aYear}-${month.month}-01`).day());
      setCalendarMonth(month.month);
      // setSelectedUser(data.workerList[0]);
      setSearchCheck(true);
      getMonthlyAttendListAPI(wCdParam);
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 검색 버튼 클릭
  const onClickSearch = () => {
    if (searchOption.wCd) {
      if (!openModal.status) {
        setLoading(true);
        navigate({ pathname: location.pathname, search: '' });
        // 근로자 조회 API
        getWorkerListAPI(searchOption.wCd);
      }
    } else toast.warning(t('근로자를 검색하세요'));
  };

  const initiateSearchOptions = () => {
    setSjCd({ type: 'sjCd', sjCd: '', cdName: '' });
    setWPrejobtype({ type: 'wPrejobtype', wPrejobtype: '', cdName: '' });
    setWJobtype({ type: 'wJobtype', wJobtype: '', cdName: '' });
    setAYear({ type: 'aYear', aYear: toDay.substring(0, 4), cdName: toDay.substring(0, 4) });
    setMonth({ type: 'month', month: toDay.substring(4, 6), cdName: toDay.substring(4, 6) });
    setSelectedUser(null); // 선택된 근로자 초기화
  };

  // 초기화 버튼 클릭
  const onClickInitiateSearchOption = () => {
    Object.keys(searchOption).map((el: any) => {
      return setSearchOption((prev: ISearchOption) => ({ ...prev, [el]: '', aYear: toDay.substring(0, 4), month: toDay.substring(4, 6) }));
    });
    setinitiate(`${Math.random()}`);
    initiateSearchOptions();
  };

  const componentRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });
  // 인쇄 버튼 클릭
  const onClickPrint = () => {
    handlePrint();
  };

  const renderPrintBtn = () => {
    if (auth.printAuth) {
      return (
        <BtnGhost onClick={onClickPrint}>
          <span className='material-symbols-rounded'>print</span>
          {t('인쇄')}
        </BtnGhost>
      );
    }
    return null;
  };

  // 사진보기 체크박스 클릭
  const handleCheckboxChange = () => {
    setPhotoViewYn(!photoViewYn);
  };

  const onClickTab = (tab: string) => {
    setCurrentTabMrCd(tab);
  };

  // 근로자명 입력창에서 엔터입력시 검색
  const loginOnEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (wNameRef.current) onClickSearch();
    }
  };

  const onClickDate = (dateParam: string) => {
    const newArrayObj: any = dateArray.find((el: any) => el.date === dateParam);
    if (newArrayObj) {
      // 해당날짜의 출근시간 또는 퇴근시간 데이터가 있을 때만 모달띄우기
      if (newArrayObj.aTimeMax || newArrayObj.aTimeMin) {
        const searchYmd = `${aYear.aYear}-${month.month}-${dateParam}`;
        const data = {
          sjName: selectedUser.sjName,
          startDate: searchYmd,
          endDate: searchYmd,
          wCd: selectedUser.wCd,
        };
        setOpenModal((prev) => ({ ...prev, status: true, type: 'attendDailyPersonalList', searchOption: data }));
      }
    }
  };

  const onClickClose = () => {
    setOpenModal((prev: any) => ({ ...prev, status: false }));
  };

  const onClickSearchWorkerBtn = () => {
    setOpenModal((prev: any) => ({ ...prev, status: true, type: 'workerList' }));
  };

  // 년/월 선택 핸들러
  const handleMonthSelect = useCallback(
    (year: string, m: string) => {
      setMonth({ type: 'month', month: m, cdName: m });

      setMonthTitle(dayjs(`${year}-${m}-01`).format(i18n.language === 'en-US' ? `YYYYMMMM` : `YYYY년 MM월`));
      setAYear({ type: 'aYear', aYear: year, cdName: year });

      if (selectedUser) searchMonthAttendAPI(selectedUser.wCd, year, m);
      else toast.warn(t('근로자를 선택하세요'));

      setShowMonthPicker(false);
    },
    [selectedUser, month.month]
  );

  // 외부 클릭 시 팝업 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowMonthPicker(false);
        setSelectedYear({ type: 'aYear', aYear: aYear.aYear, cdName: aYear.cdName }); // 이전 연도로 적용
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [aYear.aYear]);

  // 월별 출역 엑셀 다운로드
  const onClickExcelSave = async () => {
    if (!selectedUser) return toast.warn(t('근로자를 선택하세요'));

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('월 출역현황 캘린더'); // 캘린더 탭
    const worksheet2 = workbook.addWorksheet('월 출역현황 리스트'); // 리스트 탭
    const firstDay = new Date(Number(aYear.aYear), Number(month.month) - 1, 1).getDay();
    const lastDate = new Date(Number(aYear.aYear), Number(month.month), 0).getDate();
    const monthLength = Array.from({ length: lastDate }, (_, idx) => idx + 1).length;
    let currentDay = 1;

    // 1. 이미지 변환 함수
    const getBase64FromUrl = async (url: any) => {
      try {
        if (!url) return null;
        const response = await fetch(url, { cache: 'no-cache' }); // 캐시 방지
        // const response = await fetch(`${url}?t=${new Date().getTime()}`);
        if (!response.ok) return null;
        const blob = await response.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            let base64: any = reader.result;

            // 만약 헤더가 application/octet-stream이면 image/png(또는 jpeg)로 교체
            if (base64 && base64.includes('application/octet-stream')) {
              base64 = base64.replace('application/octet-stream', 'image/jpeg'); // png나 jpeg로 교체
            }
            resolve(base64);
          };
          reader.readAsDataURL(blob);
        });
      } catch (e) {
        return null;
      }
    };

    // 2. 데이터 병렬 준비 (no-await-in-loop 회피)
    const preparedData: any = await Promise.all(
      dateArray.map(async (item: any) => {
        const base64: any = await getBase64FromUrl(item.aImg);
        return { ...item, base64: photoViewYn ? base64 : null };
      })
    );

    // 3-1. 출역 리스트 설정
    worksheet2.addRow([`${aYear.aYear}년 ${month.month}월 ${selectedUser.wName ?? '-'} 월별 출역 리스트`]);
    worksheet2.mergeCells('A1:D1');
    worksheet2.getRow(1).height = 40;
    const listTitleCell = worksheet2.getCell('A1');
    listTitleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    listTitleCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF16365C' },
    };
    listTitleCell.font = {
      size: 16,
      bold: true,
      color: { argb: 'FFFFFFFF' },
    };
    worksheet2.addRow(['협력업체', selectedUser.sjName ?? '-', '직종', selectedUser.wJobtypeName ?? '-']);
    const listMdTitleRows = worksheet2.getRow(2);
    listMdTitleRows.height = 32;
    const listMdTitleFont = {
      size: 12,
      color: { argb: 'FF000000' },
    };

    for (let i = 1; i <= 4; i += 1) {
      const listMdTitleCell = listMdTitleRows.getCell(i);

      listMdTitleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFFFFF' },
      };

      listMdTitleCell.alignment = {
        horizontal: 'center',
        vertical: 'middle',
      };

      listMdTitleCell.border = {
        top: { style: 'thin' },
        right: { style: 'thin' },
      };

      if (i === 1) {
        listMdTitleCell.font = { ...listMdTitleFont, bold: true };
        listMdTitleCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD9D9D9' },
        };
        listMdTitleCell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
      }
      if (i === 3) {
        listMdTitleCell.font = { ...listMdTitleFont, bold: true };
        listMdTitleCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD9D9D9' },
        };
      }
    }
    worksheet2.addRow(['날짜', '출역사진', '최초 출역시간', '최종 출역시간']);
    worksheet2.getRow(3).height = 28;
    const listHeadRows = worksheet2.getRow(3);
    for (let i = 1; i <= 4; i += 1) {
      const listHeadCell = listHeadRows.getCell(i);
      listHeadCell.alignment = { horizontal: 'center', vertical: 'middle' };
      listHeadCell.font = {
        size: 12,
        bold: true,
      };
      listHeadCell.border = {
        top: { style: 'thin' },
        right: { style: 'thin' },
      };
      listHeadCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD9D9D9' },
      };
      if (i === 1) {
        listHeadCell.border = {
          left: { style: 'thin' },
          right: { style: 'thin' },
          top: { style: 'thin' },
        };
      }
    }

    for (let i = 1; i <= monthLength; i += 1) {
      const dateRow = worksheet2.getRow(i + 3);
      dateRow.height = 80;
      const dayData: any = preparedData.find((item: any) => Number(item.date) === i);

      for (let j = 1; j <= 4; j += 1) {
        const eachCell = dateRow.getCell(j);

        eachCell.alignment = { horizontal: 'center', vertical: 'middle' };
        eachCell.border = {
          top: { style: 'thin' },
          right: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
        };

        if (j === 1) eachCell.value = `${month.month}/${dayData.date ?? '-'}`;
        if (j === 3) eachCell.value = dayData.aTimeMin ?? '-';
        if (j === 4) eachCell.value = dayData.aTimeMax ?? '-';

        if (dayData?.base64) {
          const { base64 } = dayData;
          const extension: any = dayData.aImg.split('.').pop().toLowerCase() || 'png';

          const imageId = workbook.addImage({
            base64,
            extension: extension === 'jpg' ? 'jpeg' : extension,
          });

          // 순서 0부터 시작
          worksheet2.addImage(imageId, {
            tl: {
              col: 1 + 0.65, // 셀 왼쪽 여백
              row: i + 2 + 0.1, // 날짜 아래 행 시작 위치
            },
            ext: { width: 60, height: 90 },
          });
        }
      }
    }

    // 3-2. 캘린더 설정
    worksheet.addRow([`${aYear.aYear}년 ${month.month}월`]);
    worksheet.mergeCells('A1:G1');
    worksheet.getRow(1).height = 52;
    const titleCell = worksheet.getCell('A1');
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.font = {
      name: '맑은 고딕',
      family: 2,
      size: 16,
      underline: false,
      bold: true,
      color: { argb: 'FFFFFFFF' },
    };
    titleCell.border = {
      top: { style: 'thick' },
      left: { style: 'thick' },
      bottom: { style: 'thin' },
      right: { style: 'thick' },
    };
    titleCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF16365C' },
    };

    // 중간 title 추가
    worksheet.addRow(['협력업체', selectedUser.sjName ?? '-', '', '직종', selectedUser.wJobtypeName ?? '-', '근로자명', selectedUser.wName ?? '-']);
    worksheet.mergeCells('B2:C2');
    const middleTitleRows = worksheet.getRow(2);
    middleTitleRows.height = 32;
    const middleTitleFont = {
      size: 12,
      color: { argb: 'FF000000' },
    };

    for (let i = 1; i <= 7; i += 1) {
      const middleTitleCell = middleTitleRows.getCell(i);

      middleTitleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFFFFF' },
      };

      middleTitleCell.alignment = {
        horizontal: 'center',
        vertical: 'middle',
      };

      middleTitleCell.border = {
        top: { style: 'thin' },
        right: { style: 'thin' },
      };

      if (i === 1) {
        middleTitleCell.font = { ...middleTitleFont, bold: true };
        middleTitleCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD9D9D9' },
        };
        middleTitleCell.border = {
          top: { style: 'thin' },
          left: { style: 'thick' },
          right: { style: 'thin' },
        };
      }
      if (i === 4 || i === 6) {
        middleTitleCell.font = { ...middleTitleFont, bold: true };
        middleTitleCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD9D9D9' },
        };
      }
      if (i === 7) {
        middleTitleCell.border = {
          top: { style: 'thin' },
          right: { style: 'thick' },
        };
      }
    }

    // 주간 요일 헤더 셀 설정
    const weekDays = ['일', '월', '화', '수', '목', '금', '토'];
    worksheet.addRow(weekDays);
    const dayRows = worksheet.getRow(3);
    dayRows.height = 32;
    const cellFont = {
      name: '맑은 고딕',
      family: 2,
      size: 12,
      underline: false,
      bold: true,
    };
    for (let i = 1; i <= 7; i += 1) {
      const weekDayCell = dayRows.getCell(i);

      weekDayCell.font = {
        ...cellFont,
        color: { argb: 'FF000000' },
      };

      weekDayCell.border = {
        top: { style: 'thin' },
      };

      if (i === 1) {
        weekDayCell.font = { ...cellFont, color: { argb: 'FFFF0000' } };
        weekDayCell.border = {
          top: { style: 'thin' },
          left: { style: 'thick' },
        };
      }
      if (i === 7) {
        weekDayCell.font = { ...cellFont, color: { argb: 'FF0000FF' } };
        weekDayCell.border = {
          top: { style: 'thin' },
          right: { style: 'thick' },
        };
      }

      weekDayCell.alignment = {
        horizontal: 'center',
        vertical: 'middle',
      };

      weekDayCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD9D9D9' },
      };
    }

    // 달력은 최대 6주(rowIdx 0~5)까지 가능
    for (let rowIdx = 0; rowIdx < 6; rowIdx += 1) {
      const rowNumber = rowIdx * 2 + 4;
      const dateRow = worksheet.getRow(rowNumber);
      const imgRow = worksheet.getRow(rowNumber + 1);
      const isLastWeek = (currentDay + (6 - firstDay) >= lastDate && rowIdx > 0) || currentDay + 6 >= lastDate;
      imgRow.height = 100;

      for (let colIdx = 0; colIdx < 7; colIdx += 1) {
        const dateCell = dateRow.getCell(colIdx + 1);
        const imgCell = imgRow.getCell(colIdx + 1);

        dateCell.border = {
          top: { style: 'thin' },
          right: { style: 'thin' },
        };

        imgCell.border = {
          right: { style: 'thin' },
        };

        // 마지막 주 border 추가
        if (isLastWeek) {
          imgCell.border = {
            right: { style: 'thin' },
            bottom: { style: 'thick' },
          };
        }

        // 첫 주 시작 요일 이후이고, 아직 마지막 날짜를 넘지 않았을 때만 실행
        const isStarted = rowIdx > 0 || colIdx >= firstDay;
        const isNotFinished = currentDay <= lastDate;

        if (isStarted && isNotFinished) {
          // 1. 날짜 입력
          dateCell.value = currentDay;
          dateCell.alignment = { horizontal: 'center', vertical: 'middle' };
          dateCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF2F2F2' },
          };
          if (colIdx === 0) {
            dateCell.font = { ...dateCell.font, color: { argb: 'FFFF0000' } };
            dateCell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFF2DCDB' },
            };
            dateCell.border = {
              top: { style: 'thin' },
              left: { style: 'thick' },
              right: { style: 'thin' },
            };
          }
          if (colIdx === 6) {
            dateCell.font = { ...dateCell.font, color: { argb: 'FF0000FF' } };
            dateCell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFDCE5F1' },
            };
            dateCell.border = {
              top: { style: 'thin' },
              right: { style: 'thick' },
            };
          }

          // 2. 이미지 처리 데이터 찾기
          const dayData: any = preparedData.find((item: any) => Number(item.date) === currentDay);

          // 시간 텍스트 처리
          const timeText = dayData?.aTimeMin || dayData?.aTimeMax ? `${dayData.aTimeMin || '-'}/${dayData.aTimeMax || '-'}` : '-';
          imgCell.value = timeText;

          if (colIdx === 0) {
            imgCell.border = {
              left: { style: 'thick' },
              right: { style: 'thin' },
            };

            if (isLastWeek) {
              imgCell.border = {
                left: { style: 'thick' },
                right: { style: 'thin' },
                bottom: { style: 'thick' },
              };
            }
          }
          if (colIdx === 6) {
            imgCell.border = {
              right: { style: 'thick' },
            };

            if (isLastWeek) {
              imgCell.border = {
                right: { style: 'thick' },
                bottom: { style: 'thick' },
              };
            }
          }

          if (dayData?.base64) {
            imgCell.alignment = { horizontal: 'center', vertical: 'bottom' };

            const { base64 } = dayData;
            const extension: any = dayData.aImg.split('.').pop().toLowerCase() || 'png';

            const imageId = workbook.addImage({
              base64,
              extension: extension === 'jpg' ? 'jpeg' : extension,
            });

            // tl: { col: 0-based 열 번호, row: 0-based 행 번호 }
            // 엑셀의 imgRowNumber(예: 3행)에 넣으려면 row 인덱스는 2여야 하므로 rowNumber 사용
            worksheet.addImage(imageId, {
              tl: {
                col: colIdx + 0.65, // 셀 왼쪽 여백
                row: rowNumber + 0.1, // 날짜 아래 행 시작 위치
              },
              ext: { width: 60, height: 90 },
            });
          } else {
            imgCell.alignment = { horizontal: 'center', vertical: 'middle' };
          }

          // 3. 날짜 증가
          currentDay += 1;
        } else {
          if (colIdx === 0) {
            dateCell.border = {
              top: { style: 'thin' },
              left: { style: 'thick' },
              right: { style: 'thin' },
            };
            imgCell.border = {
              left: { style: 'thick' },
              right: { style: 'thin' },
            };
            if (isLastWeek) {
              imgCell.border = {
                left: { style: 'thick' },
                right: { style: 'thin' },
                bottom: { style: 'thick' },
              };
            }
          }
          if (colIdx === 6) {
            dateCell.border = {
              top: { style: 'thin' },
              right: { style: 'thick' },
            };
            imgCell.border = {
              right: { style: 'thick' },
            };

            if (isLastWeek) {
              imgCell.border = {
                right: { style: 'thick' },
                bottom: { style: 'thick' },
              };
            }
          }
        }
      }
      // 모든 날짜를 다 채웠으면 바깥 루프도 종료
      if (currentDay > lastDate) break;
    }

    worksheet.columns.forEach((col: any) => {
      col.width = 18;
    });

    worksheet2.columns.forEach((col: any) => {
      col.width = 20;
    });

    // 5. 다운로드
    const buffer = await workbook.xlsx.writeBuffer();
    return saveAs(new Blob([buffer]), `${searchOption.wName}-${aYear.aYear}-${month.month}-월 출역현황.xlsx`);
  };

  return (
    <div className='contents'>
      <div className='content-container oneColumn'>
        <Tab tabList={newTabList || tabInfo.tabList} currentTabMrCd={currentTabMrCd} setCurrentTabMrCd={setCurrentTabMrCd} onClickTab={onClickTab} size={size} />
        <Root loading={loading}>
          <SearchOptions className='border-bottom'>
            {size.innerSize.W > 1024 ? (
              <>
                {isBottom && (
                  <FloatingButtonToTop>
                    <button type='button' onClick={() => scrollToNodeTop(tableRef)}>
                      <IoChevronUpSharp size={20} style={{ stroke: 'white' }} />
                    </button>
                  </FloatingButtonToTop>
                )}
                <div className='inputsWrapper'>
                  <div className='inputForm-row withLabel'>
                    <label htmlFor='sjCd'>{t('협력업체')}</label>
                    <div className='inputForm-col'>
                      <SearchSelectBoxSm
                        options={siteJoinInfoList}
                        defaultOption={t('전체')}
                        state={sjCd}
                        setState={setSjCd}
                        stateKey='sjCd'
                        codeKey='cdName'
                        filterbar='filter-1-left'
                        optionHeight='height-md'
                      />
                    </div>
                  </div>
                  {/* <div className='inputForm-row'>
                <div className='inputForm-col'>
                  <SelectBox
                    options={YEAR_LIST}
                    defaultOption={aYear.aYear}
                    state={aYear}
                    setState={setAYear}
                    stateKey='aYear'
                    initiateKey={initiate}
                    filterbar='filter-1-left'
                    optionHeight='height-md'
                  />
                </div>
              </div>
              <div className='inputForm-row'>
                <div className='inputForm-col'>
                  <SelectBox
                    options={MONTH_LIST}
                    defaultOption={month.month}
                    state={month}
                    setState={setMonth}
                    stateKey='month'
                    initiateKey={initiate}
                    filterbar='filter-1-left'
                    optionHeight='height-sm'
                  />
                </div>
              </div> */}
                  {userInfo.prejobtypeYn === 'Y' && (
                    <div className='inputForm-row'>
                      <div className='inputForm-col'>
                        <SearchSelectBoxSm
                          options={prejobtypeList}
                          defaultOption={t('공종 전체')}
                          state={wPrejobtype}
                          setState={setWPrejobtype}
                          stateKey='wPrejobtype'
                          codeKey='cdName'
                          initiateKey={initiate}
                          filterbar='filter-1-leftToCenter'
                          comboWidth='expand-box-sm'
                          optionHeight='height-md'
                        />
                      </div>
                    </div>
                  )}
                  <div className='inputForm-row'>
                    <div className='inputForm-col'>
                      <SearchSelectBoxSm
                        options={jikJongList}
                        defaultOption={t('직종 전체')}
                        state={wJobtype}
                        setState={setWJobtype}
                        stateKey='wJobtype'
                        codeKey='cdName'
                        initiateKey={wJobtype.wJobtype}
                        filterbar='filter-1-center'
                        comboWidth='expand-box-sm'
                        optionHeight='height-md'
                      />
                    </div>
                  </div>
                  {/* <div className='inputForm-row'>
                <div className='inputForm-col'>
                  <Input
                    disabled
                    placeholder={t('근로자를 검색하세요')}
                    label=''
                    type='text'
                    id='wName'
                    name='wName'
                    state={searchOption}
                    set={setSearchOption}
                    inputRef={wNameRef}
                    onKeyDown={loginOnEnterKeyDown}
                  />
                </div>
              </div> */}
                  {searchOption.wName.length ? (
                    <div className='searchData'>
                      <span>{t('근로자명')}</span>
                      {/* <span className='seperator' /> */}
                      {searchOption.wName}
                    </div>
                  ) : (
                    ''
                  )}
                  <div className='btn'>
                    <div className='inputForm-row'>
                      <div className='inputForm-col'>
                        <BtnLightViolet onClick={onClickSearchWorkerBtn}>{searchOption.wName.length === 0 ? t('근로자 검색') : t('근로자 재검색')}</BtnLightViolet>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='inputsWrapper'>
                  <div className='secondSearchOption'>
                    <div className='flex-basic textBtnGroup'>
                      <ShortcutButton icon='search' buttonText='검색' shortcut='F9' onClick={onClickSearch} />
                      <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                    </div>
                    <div className='flex-basic iconBtnGroup'>
                      {/* showExcelBtn && (
                        <BtnGhost onClick={onClickExcelSave}>
                          <span className='icon-ms-xlsx'>X</span> {t('엑셀')}
                        </BtnGhost>
                      ) */}
                      <Toggle onChange={handleCheckboxChange} status={photoViewYn} text={t('사진보기')} />
                      {/* {renderPrintBtn()} */}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className='filter-wrapper'>
                <div className='inputsWrapper'>
                  <div className='inputForm-row' style={{ width: 'calc(100% - 100px)', height: '100%' }}>
                    <div className='searchData'>{searchOption.wName || '근로자 검색 필요'}</div>
                  </div>
                  <div className='btn'>
                    <div className='inputForm-row'>
                      <div className='inputForm-col'>
                        <BtnLightViolet className='worker-search-btn' onClick={onClickSearchWorkerBtn}>
                          {searchOption.wName.length === 0 ? t('근로자 검색') : t('근로자 재검색')}
                        </BtnLightViolet>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='inputsWrapper'>
                  <div className='flex-basic iconBtnGroup'>
                    <Toggle onChange={handleCheckboxChange} status={photoViewYn} text={t('사진보기')} />
                  </div>
                </div>
              </div>
            )}
          </SearchOptions>
          <div ref={componentRef}>
            <h2 className='calendarMonthTitle flex-center'>
              <div className='arrow-button flex-center' role='button' tabIndex={0} onClick={() => handleMonthChange('prev')}>
                <span className='material-symbols-rounded'>arrow_back_ios</span>
              </div>
              <div className='month-title' role='presentation' onClick={() => (selectedUser ? setShowMonthPicker(true) : toast.warn(t('근로자를 선택하세요')))}>
                {monthTitle}
              </div>
              <div className='arrow-button flex-center' role='button' tabIndex={0} onClick={() => handleMonthChange('next')}>
                <span className='material-symbols-rounded'>arrow_forward_ios</span>
              </div>
            </h2>
            {showMonthPicker && (
              <MonthPickerPopupStyle ref={datePickerRef}>
                <div className='year-month-selector'>
                  <div className='year-selector'>
                    <select value={selectedYear.aYear} onChange={(e) => setSelectedYear({ type: 'aYear', aYear: e.target.value, cdName: e.target.value })}>
                      {YEAR_LIST.map(({ aYear: year }) => (
                        <option key={year} value={year}>
                          {year}
                          {t('년')}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className='month-grid'>
                    {MONTH_LIST.map(({ month: m }) => (
                      <button
                        key={m}
                        type='button'
                        className={month.month === m ? 'selected' : ''}
                        onClick={(e) => {
                          e.stopPropagation(); // 이벤트 버블링 방지
                          handleMonthSelect(selectedYear.aYear, m);
                        }}
                      >
                        {m}
                        {t('월')}
                      </button>
                    ))}
                  </div>
                </div>
              </MonthPickerPopupStyle>
            )}

            <div className='calendar' style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {days.map((day) => (
                <div
                  className='days'
                  key={day}
                  style={{
                    color:
                      day === 'Sun'
                        ? theme === 'light'
                          ? 'var(--rose-500)'
                          : 'var(--rose-400)'
                        : day === 'Sat'
                        ? theme === 'light'
                          ? 'var(--blue-500)'
                          : 'var(--blue-400)'
                        : theme === 'light'
                        ? light.text_primary
                        : dark.text_primary,
                    borderLeft: day === 'Sun' ? 'none' : '1px solid var(--outline)',
                  }}
                >
                  {day}
                </div>
              ))}
              {[...new Array(firstDayOfMonth)].map((e, i) => (
                <div className='emptyCells' key={`empty-${i}`} style={{ borderLeft: i === 0 ? 'none' : '1px solid var(--outline)' }} />
              ))}
              {dateArray.map(({ date, dayOfWeek, aTimeMin, aTimeMax, aImg }: any, index: number) => (
                <div
                  key={index}
                  className='date'
                  onClick={() => onClickDate(date)}
                  role='presentation'
                  style={{
                    borderLeft: dayOfWeek === 0 ? 'none' : '1px solid var(--outline)',
                  }}
                >
                  <div>
                    <div>
                      <div
                        style={{
                          color:
                            dayOfWeek === 0
                              ? theme === 'light'
                                ? 'var(--rose-500)'
                                : 'var(--rose-400)'
                              : dayOfWeek === 6
                              ? theme === 'light'
                                ? 'var(--blue-500)'
                                : 'var(--blue-400)'
                              : theme === 'light'
                              ? light.text_primary
                              : dark.text_primary,
                        }}
                      >
                        {date}
                      </div>
                      {photoViewYn ? (
                        aTimeMax !== '' ? (
                          aImg !== '' && aImg !== null ? (
                            <img style={{ display: !photoViewYn ? 'none' : 'block' }} src={aImg} alt='' width='100%' />
                          ) : (
                            <div className='flex-center' style={{ paddingTop: '1rem' }}>
                              No Image
                            </div>
                          )
                        ) : null
                      ) : null}
                    </div>
                    <div className='flex-between'>
                      <div>{aTimeMin}</div>
                      <div>{aTimeMax}</div>
                    </div>
                  </div>
                </div>
              ))}
              {remainingCells < 7 && [...new Array(remainingCells)].map((e, i) => <div className='emptyCells' key={`empty-end-${i}`} />)}
            </div>
          </div>
        </Root>
      </div>
      {loading && (
        <LoadingModalBackground>
          <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
        </LoadingModalBackground>
      )}
      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'workerList' && (
          <ModalBackground>
            <WorkerListModal
              openModal={openModal}
              setOpenModal={setOpenModal}
              state={selectedUser}
              setState={setSelectedUser}
              isIncludeBlackUser
              useCheckbox={false}
              filter={{ sjCd: sjCd.sjCd, wName: searchOption.wName, wPrejobtype: wPrejobtype.wPrejobtype, wJobtype: wJobtype.wJobtype }}
              onClickClose={onClickClose}
            />
          </ModalBackground>
        )}
        {openModal.status && openModal.type === 'attendDailyPersonalList' && <AttendDailyPersonalListModal setOpenModal={setOpenModal} filter={openModal.searchOption} />}
      </Portal>
    </div>
  );
};

export default Sattend6;
